import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { caseService, userPreferencesService, userService, searchClientsAsync } from '../services/api';
import AsyncSearchableSelect from '../components/common/AsyncSearchableSelect';
import { useAuth } from '../contexts/AuthContext';
import CreateCaseModal from '../components/CreateCaseModal';
import {
  Briefcase,
  Plus,
  Search,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  SlidersHorizontal,
  Lock,
  Download,
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const DEFAULT_COLUMNS = {
  case_number: true,
  title: true,
  client_name: true,
  case_type: true,
  attorney: true,
  creator: false,
  created_date: true,
  next_task: true,
  due_date: true,
  assigned_to: true,
  progress: true,
};

const COLUMN_DEFS = [
  { key: 'case_number', label: 'Case #', sortable: true, alwaysVisible: true },
  { key: 'title', label: 'Case Name', sortable: true, alwaysVisible: true },
  { key: 'client_name', label: 'Client', sortable: true },
  { key: 'case_type', label: 'Type', sortable: true },
  { key: 'attorney', label: 'Attorney', sortable: true },
  { key: 'creator', label: 'Creator', sortable: true },
  { key: 'created_date', label: 'Created', sortable: true },
  { key: 'next_task', label: 'Next Task', sortable: false },
  { key: 'due_date', label: 'Due Date', sortable: true },
  { key: 'assigned_to', label: 'Assigned To', sortable: false },
  { key: 'progress', label: 'Progress', sortable: true },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
  { value: '', label: 'All' },
];

const SORT_KEY_MAP = {
  case_number: 'case_number',
  title: 'title',
  client_name: 'client_name',
  case_type: 'case_type',
  attorney: 'attorney',
  creator: 'creator',
  created_date: 'created_date',
  due_date: 'next_task_due',
  progress: 'progress',
};

// Defined at module level to prevent re-mount on parent re-render
const SortIcon = ({ columnKey, sortBy, sortDirection }) => {
  const mapped = SORT_KEY_MAP[columnKey] || columnKey;
  if (sortBy === mapped) {
    return sortDirection === 'asc'
      ? <ChevronUp className="inline h-4 w-4 ml-1" />
      : <ChevronDown className="inline h-4 w-4 ml-1" />;
  }
  return <ChevronsUpDown className="inline h-4 w-4 ml-1 text-gray-300" />;
};

const Cases = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [actionBanner, setActionBanner] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  // Handle quick-action state from Navbar
  useEffect(() => {
    const openAction = location.state?.openAction;
    if (openAction === 'new-note') {
      setActionBanner('Select a case to add a note');
      setPendingAction('new-note');
      navigate(location.pathname, { replace: true, state: {} });
    } else if (openAction === 'new-task') {
      setActionBanner('Select a case to add a task');
      setPendingAction('new-task');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Data
  const [cases, setCases] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);     // initial full-page load
  const [refreshing, setRefreshing] = useState(false); // subsequent background fetches

  // Filters
  const [statusFilter, setStatusFilter] = useState('active');
  const [caseTypeFilter, setCaseTypeFilter] = useState('');
  const [attorneyFilter, setAttorneyFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [searchText, setSearchText] = useState('');       // input display value
  const [debouncedSearch, setDebouncedSearch] = useState(''); // actual query value
  const searchDebounceRef = useRef(null);

  // Sort
  const [sortBy, setSortBy] = useState('created_date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMNS);

  // UI
  const [refreshCounter, setRefreshCounter] = useState(0); // bump to re-trigger fetch
  const [showNewCase, setShowNewCase] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const columnSettingsRef = useRef(null);

  // Dropdown data
  const [attorneys, setAttorneys] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);

  // Preferences save debounce
  const prefsSaveRef = useRef(null);

  // Load dropdown data
  useEffect(() => {
    if (!isAuthenticated || authLoading) return;

    const loadDropdowns = async () => {
      try {
        const usersRes = await userService.getUsers();
        const activeUsers = (Array.isArray(usersRes.data) ? usersRes.data : []).filter(u => u.firm_active !== false && !u.has_pending_invitation);
        setAttorneys(activeUsers);
      } catch (err) {
        console.error('Error loading dropdown data:', err);
      }
    };
    loadDropdowns();
  }, [isAuthenticated, authLoading]);

  // Load user preferences on mount
  useEffect(() => {
    if (!isAuthenticated || authLoading) return;

    const loadPrefs = async () => {
      try {
        const res = await userPreferencesService.getCasesTableSettings();
        const data = res.data?.preference_data || res.data;
        if (data) {
          if (data.visible_columns) {
            setVisibleColumns(prev => ({ ...prev, ...data.visible_columns }));
          }
          if (data.sort_by) setSortBy(data.sort_by);
          if (data.sort_direction) setSortDirection(data.sort_direction);
        }
      } catch {
        // 404 = no preferences yet, use defaults
      } finally {
        setPrefsLoaded(true);
      }
    };
    loadPrefs();
  }, [isAuthenticated, authLoading]);

  // Save preferences (debounced)
  const savePrefs = useCallback((cols, sb, sd) => {
    if (prefsSaveRef.current) clearTimeout(prefsSaveRef.current);
    prefsSaveRef.current = setTimeout(async () => {
      try {
        await userPreferencesService.updateCasesTableSettings({
          visible_columns: cols,
          sort_by: sb,
          sort_direction: sd,
        });
      } catch (err) {
        console.error('Error saving preferences:', err);
      }
    }, 1000);
  }, []);

  // Fetch cases — full spinner on first load, subtle indicator on subsequent
  const hasFetchedOnce = useRef(false);
  const fetchIdRef = useRef(0); // Track the latest fetch to ignore stale responses

  // Fetch when filters/sort change (but wait for prefs to load first)
  useEffect(() => {
    if (!prefsLoaded || !isAuthenticated || authLoading) return;

    const fetchId = ++fetchIdRef.current;

    if (hasFetchedOnce.current) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    const params = {
      sort_by: sortBy,
      sort_direction: sortDirection,
      skip: 0,
      limit: 100,
    };
    if (statusFilter) params.status_filter = statusFilter;
    if (caseTypeFilter) params.case_type_filter = caseTypeFilter;
    if (attorneyFilter) params.attorney_id = attorneyFilter;
    if (clientFilter) params.client_id = clientFilter;
    if (debouncedSearch) params.search = debouncedSearch;

    caseService.getCasesTableView(params)
      .then(res => {
        // Ignore stale responses from superseded fetches
        if (fetchId !== fetchIdRef.current) return;

        setCases(res.data.cases || []);
        setTotalCount(res.data.total_count || 0);
        hasFetchedOnce.current = true;

        // Extract unique case types for the filter dropdown
        const types = [...new Set((res.data.cases || []).map(c => c.case_type).filter(Boolean))];
        setCaseTypes(prev => {
          const merged = [...new Set([...prev, ...types])].sort();
          return merged;
        });
      })
      .catch(err => {
        if (fetchId !== fetchIdRef.current) return;
        console.error('Error fetching cases:', err);
        setCases([]);
        setTotalCount(0);
      })
      .finally(() => {
        if (fetchId !== fetchIdRef.current) return;
        setLoading(false);
        setRefreshing(false);
      });
  }, [prefsLoaded, isAuthenticated, authLoading, sortBy, sortDirection, statusFilter, caseTypeFilter, attorneyFilter, clientFilter, debouncedSearch, refreshCounter]);

  // Debounced search — updates debouncedSearch after 300ms of no typing
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(val);
    }, 300);
  };

  // Sort handler
  const handleSort = (columnKey) => {
    const col = COLUMN_DEFS.find(c => c.key === columnKey);
    if (!col || !col.sortable) return;

    const newSortBy = SORT_KEY_MAP[columnKey] || columnKey;

    let newDir = 'asc';
    if (sortBy === newSortBy) {
      newDir = sortDirection === 'asc' ? 'desc' : 'asc';
    }
    setSortBy(newSortBy);
    setSortDirection(newDir);
    savePrefs(visibleColumns, newSortBy, newDir);
  };

  // Column toggle
  const toggleColumn = (key) => {
    const col = COLUMN_DEFS.find(c => c.key === key);
    if (col?.alwaysVisible) return;
    const updated = { ...visibleColumns, [key]: !visibleColumns[key] };
    setVisibleColumns(updated);
    savePrefs(updated, sortBy, sortDirection);
  };

  // Close column settings on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (columnSettingsRef.current && !columnSettingsRef.current.contains(e.target)) {
        setShowColumnSettings(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Due date color
  const getDueDateClasses = (dueDateStr) => {
    if (!dueDateStr) return 'text-gray-500';
    const due = new Date(dueDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDay = new Date(due);
    dueDay.setHours(0, 0, 0, 0);

    if (dueDay < today) return 'text-red-600 font-medium';
    if (dueDay.getTime() === today.getTime()) return 'text-amber-600 font-medium';
    return 'text-gray-600';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Render cell by column key
  const renderCell = (caseItem, colKey) => {
    switch (colKey) {
      case 'case_number':
        return (
          <span className="text-sm font-medium text-gray-900 inline-flex items-center gap-1">
            {caseItem.case_number}
            {caseItem.visibility_mode && caseItem.visibility_mode !== 'firm' && (
              <Lock className="w-3 h-3 text-orange-500" title="Restricted visibility" />
            )}
          </span>
        );
      case 'title':
        return <span className="text-sm font-medium text-gray-900">{caseItem.title}</span>;
      case 'client_name':
        return <span className="text-sm text-gray-600">{caseItem.client_name || '-'}</span>;
      case 'case_type':
        return <span className="text-sm text-gray-600 capitalize">{caseItem.case_type || '-'}</span>;
      case 'attorney':
        return <span className="text-sm text-gray-600">{caseItem.attorney_name || '-'}</span>;
      case 'creator':
        return <span className="text-sm text-gray-600">{caseItem.creator_name || '-'}</span>;
      case 'created_date':
        return <span className="text-sm text-gray-600">{formatDate(caseItem.created_at)}</span>;
      case 'next_task':
        return <span className="text-sm text-gray-600">{caseItem.next_task_title || '-'}</span>;
      case 'due_date':
        return (
          <span className={`text-sm ${getDueDateClasses(caseItem.next_task_due)}`}>
            {formatDate(caseItem.next_task_due)}
          </span>
        );
      case 'assigned_to':
        return <span className="text-sm text-gray-600">{caseItem.next_task_assignee || '-'}</span>;
      case 'progress':
        return (
          <div className="flex items-center space-x-2">
            <div className="w-20 bg-gray-200 rounded-full h-2 overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 rounded-full"></div>
              <div
                className="absolute inset-0 bg-gray-200 rounded-full transition-all duration-300"
                style={{ left: `${caseItem.progress_percentage}%` }}
              ></div>
            </div>
            <span className="text-xs text-gray-600 w-8">{caseItem.progress_percentage}%</span>
          </div>
        );
      default:
        return '-';
    }
  };

  const handleCaseCreated = () => {
    setShowNewCase(false);
    setRefreshCounter(c => c + 1);
  };

  // Export currently filtered case list to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });

    // Title
    const activeFilter = STATUS_OPTIONS.find(o => o.value === statusFilter);
    const filterLabel = activeFilter ? activeFilter.label : 'All';
    doc.setFontSize(16);
    doc.text(`Cases — ${filterLabel}`, 14, 18);

    // Subtitle with applied filters
    const filterParts = [];
    if (attorneyFilter) {
      const att = attorneys.find(a => a.id === attorneyFilter);
      if (att) filterParts.push(`Attorney: ${att.first_name} ${att.last_name}`);
    }
    if (caseTypeFilter) filterParts.push(`Type: ${caseTypeFilter}`);
    if (debouncedSearch) filterParts.push(`Search: "${debouncedSearch}"`);
    if (filterParts.length > 0) {
      doc.setFontSize(10);
      doc.text(filterParts.join('  |  '), 14, 25);
    }

    doc.setFontSize(8);
    doc.text(`Generated ${new Date().toLocaleString()}  —  ${cases.length} case${cases.length !== 1 ? 's' : ''}`, 14, filterParts.length > 0 ? 30 : 25);

    // Build columns from visible columns + status (always shown)
    const pdfColumns = [...activeColumns.map(c => c.label), 'Status'];

    // Build rows from currently loaded cases
    const getCellText = (caseItem, colKey) => {
      switch (colKey) {
        case 'case_number': return caseItem.case_number || '-';
        case 'title': return caseItem.title || '-';
        case 'client_name': return caseItem.client_name || '-';
        case 'case_type': return (caseItem.case_type || '-').replace(/_/g, ' ');
        case 'attorney': return caseItem.attorney_name || '-';
        case 'creator': return caseItem.creator_name || '-';
        case 'created_date': return caseItem.created_at ? new Date(caseItem.created_at).toLocaleDateString() : '-';
        case 'next_task': return caseItem.next_task_title || '-';
        case 'due_date': return caseItem.next_task_due ? new Date(caseItem.next_task_due).toLocaleDateString() : '-';
        case 'assigned_to': return caseItem.next_task_assignee || '-';
        case 'progress': return `${caseItem.progress_percentage}%`;
        default: return '-';
      }
    };

    const pdfRows = cases.map(c => [
      ...activeColumns.map(col => getCellText(c, col.key)),
      c.status || '-',
    ]);

    autoTable(doc, {
      head: [pdfColumns],
      body: pdfRows,
      startY: filterParts.length > 0 ? 34 : 29,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [234, 179, 8], textColor: [0, 0, 0], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [249, 250, 251] },
    });

    doc.save(`cases-${filterLabel.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-600">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  const activeColumns = COLUMN_DEFS.filter(c => visibleColumns[c.key]);

  return (
    <div className="space-y-6">
      {/* Action Banner from Quick Actions */}
      {actionBanner && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center justify-between">
          <span className="text-sm text-yellow-800 font-medium">{actionBanner}</span>
          <button onClick={() => setActionBanner(null)} className="text-yellow-600 hover:text-yellow-800 text-sm">Dismiss</button>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Briefcase className="mr-3 h-8 w-8" />
            Cases
          </h1>
          <p className="text-gray-600">
            {totalCount} case{totalCount !== 1 ? 's' : ''} found
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportPDF}
            className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg flex items-center hover:bg-gray-50"
            title="Export filtered cases to PDF"
          >
            <Download className="mr-2 h-4 w-4" />
            Export PDF
          </button>
          <button
            onClick={() => setShowNewCase(true)}
            className="bg-yellow-500 text-white px-4 py-2 rounded-lg flex items-center hover:bg-yellow-600"
          >
            <Plus className="mr-2 h-4 w-4" />
            New Case
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Status pills */}
          <div className="flex items-center space-x-1">
            {STATUS_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={`px-3 py-1.5 text-sm rounded-full font-medium transition-colors ${
                  statusFilter === opt.value
                    ? 'bg-green-100 text-green-800 ring-1 ring-green-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Separator */}
          <div className="h-8 w-px bg-gray-200"></div>

          {/* Case type dropdown */}
          <select
            value={caseTypeFilter}
            onChange={(e) => setCaseTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          >
            <option value="">All Types</option>
            {caseTypes.map(t => (
              <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
            ))}
          </select>

          {/* Attorney dropdown */}
          <select
            value={attorneyFilter}
            onChange={(e) => setAttorneyFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          >
            <option value="">All Attorneys</option>
            {attorneys.map(a => (
              <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>
            ))}
          </select>

          {/* Client dropdown */}
          <AsyncSearchableSelect
            value={clientFilter}
            onChange={(val) => setClientFilter(val)}
            onSearch={searchClientsAsync}
            emptyOption="All Clients"
            className="min-w-[180px]"
          />

          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search cases..."
              value={searchText}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            />
          </div>

          {/* Column settings */}
          <div className="relative" ref={columnSettingsRef}>
            <button
              onClick={() => setShowColumnSettings(!showColumnSettings)}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-500"
              title="Column settings"
            >
              <SlidersHorizontal className="h-4 w-4" />
            </button>
            {showColumnSettings && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-20 p-3">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Visible Columns</h4>
                {COLUMN_DEFS.map(col => (
                  <label
                    key={col.key}
                    className={`flex items-center space-x-2 py-1.5 px-1 rounded text-sm ${
                      col.alwaysVisible ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 cursor-pointer hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={visibleColumns[col.key]}
                      disabled={col.alwaysVisible}
                      onChange={() => toggleColumn(col.key)}
                      className="rounded border-gray-300 text-yellow-500 focus:ring-yellow-500"
                    />
                    <span>{col.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {activeColumns.map(col => (
                  <th
                    key={col.key}
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      col.sortable ? 'cursor-pointer select-none hover:bg-gray-100' : ''
                    }`}
                    onClick={() => col.sortable && handleSort(col.key)}
                  >
                    {col.label}
                    {col.sortable && <SortIcon columnKey={col.key} sortBy={sortBy} sortDirection={sortDirection} />}
                  </th>
                ))}
                {/* Status column is always visible */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className={`bg-white divide-y divide-gray-200 transition-opacity duration-200 ${refreshing ? 'opacity-50' : 'opacity-100'}`}>
              {loading ? (
                <tr>
                  <td colSpan={activeColumns.length + 1} className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto mb-2"></div>
                    <p className="text-gray-500">Loading cases...</p>
                  </td>
                </tr>
              ) : cases.length > 0 ? (
                cases.map(caseItem => (
                  <tr
                    key={caseItem.id}
                    onClick={() => {
                      if (pendingAction) {
                        navigate(`/cases/${caseItem.id}`, { state: { openAction: pendingAction } });
                        setActionBanner(null);
                        setPendingAction(null);
                      } else {
                        navigate(`/cases/${caseItem.id}`);
                      }
                    }}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    {activeColumns.map(col => (
                      <td key={col.key} className="px-6 py-4 whitespace-nowrap">
                        {renderCell(caseItem, col.key)}
                      </td>
                    ))}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(caseItem.status)}`}>
                        {caseItem.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={activeColumns.length + 1} className="px-6 py-12 text-center">
                    <Briefcase className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-500 font-medium">No cases match your filters</p>
                    <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filter criteria</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Case Modal */}
      <CreateCaseModal
        isOpen={showNewCase}
        onClose={() => setShowNewCase(false)}
        onSuccess={handleCaseCreated}
      />
    </div>
  );
};

export default Cases;
